/**
 * ds-trust-marquee.js — syncs every eBay figure on the page with the eBay Reviews
 * app widget rendered elsewhere on it.
 *
 * The widget is a third-party app block hydrated client-side, so its markup is
 * not ours: we scan app-block wrappers' text for a review count and a positive
 * percentage instead of relying on the app's CSS classes. Fail-silent by
 * construction — if nothing parses within the observation window, the
 * server-rendered fallback text stays.
 *
 * That fail-silent property is what lets a real review COUNT appear on the page at
 * all. BRAND-VOICE 12.1 bans a hardcoded eBay count because it rots — the marquee
 * read 1080+ while the widget read 1,138. A scraped figure is never stale, and the
 * server-rendered fallback underneath it carries no number, so the page degrades to
 * a count-free claim instead of a wrong one. It is also why
 * `scan-sourcing-claims.mjs` stays quiet: that gate reads server HTML, and the only
 * number here is injected client-side.
 *
 * TWO OUTPUT SHAPES, 2026-10-18. A hook with a BARE `data-ds-ebay-text` attribute
 * gets the compact marquee string. A hook whose attribute carries a VALUE treats it
 * as a template and substitutes {count} and {pct} — so prose elsewhere on the page
 * can read the same live figure in its own wording. Bare attributes return "" from
 * getAttribute, which is falsy, so every pre-existing hook keeps its old behaviour
 * untouched.
 */
(() => {
  const HOOK = '[data-ds-ebay-text]';
  const OBSERVE_MS = 15000;
  const DEBOUNCE_MS = 250;
  const MIN_COUNT = 100;

  const COUNT_PATTERNS = [
    /([\d,]{2,})\s*\+?\s*(?:ebay\s*)?reviews/i,
    /reviews?\s*:?\s*\(?\s*([\d,]{2,})\s*\)?/i,
  ];
  const PERCENT_PATTERNS = [
    /(\d{1,3}(?:\.\d)?)\s*%\s*positive/i,
    /positive(?:\s*feedback)?\s*:?\s*(\d{1,3}(?:\.\d)?)\s*%/i,
  ];

  const matchFirst = (text, patterns) => {
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const findFigure = () => {
    for (const el of document.querySelectorAll('[id^="shopify-block-"]')) {
      const text = el.textContent || '';
      const rawCount = matchFirst(text, COUNT_PATTERNS);
      const rawPercent = matchFirst(text, PERCENT_PATTERNS);
      if (!rawCount || !rawPercent) continue;

      const count = parseInt(rawCount.replace(/,/g, ''), 10);
      const percent = parseFloat(rawPercent);
      if (Number.isFinite(count) && count >= MIN_COUNT && percent > 0 && percent <= 100) {
        return { count, percent };
      }
    }
    return null;
  };

  const apply = ({ count, percent }) => {
    // Floor to the nearest 10 and suffix a +, so the figure stays true as feedback
    // lands without the page appearing to change every day.
    const floored = Math.floor(count / 10) * 10;
    const pct = Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
    const compact = `${pct}% Positive · ${floored}+ eBay Reviews`;
    // Thousands separator for prose. The marquee keeps the unseparated form so its
    // label stays as narrow as possible on a phone.
    const pretty = floored.toLocaleString('en-AU');

    for (const el of document.querySelectorAll(HOOK)) {
      const template = el.getAttribute('data-ds-ebay-text');
      el.textContent = template
        ? template.replace(/\{count\}/g, pretty).replace(/\{pct\}/g, pct)
        : compact;
    }
  };

  try {
    if (!document.querySelector(HOOK)) return;

    const figure = findFigure();
    if (figure) {
      apply(figure);
      return;
    }

    let timer = null;
    const observer = new MutationObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          const found = findFigure();
          if (found) {
            observer.disconnect();
            apply(found);
          }
        } catch {
          observer.disconnect();
        }
      }, DEBOUNCE_MS);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), OBSERVE_MS);
  } catch {
    /* fallback text stays */
  }
})();
